import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { PartyPopper, Meh, AlertCircle, Droplets } from "lucide-react";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  // Format the label — daily chart uses "YYYY-MM-DD", monthly uses full ISO string
  let formattedDate;
  const dailyMatch = typeof label === "string" && /^\d{4}-\d{2}-\d{2}$/.test(label);
  if (dailyMatch) {
    const [year, month, day] = label.split("-").map(Number);
    formattedDate = new Date(year, month - 1, day).toLocaleDateString("default", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } else {
    const date = new Date(label);
    formattedDate = date.toLocaleDateString("default", {
      month: "long",
      year: "numeric",
    });
  }

  return (
    <div className="bg-white p-3 border rounded shadow-lg">
      <p className="font-bold mb-2">{formattedDate}</p>
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }}>
          {entry.name}: ${entry.value.toFixed(2)}
        </p>
      ))}
    </div>
  );
};

const SalesTarget = () => {
  // State declarations
  const [monthlyData, setMonthlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [dailyTarget, setDailyTarget] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [todayProfit, setTodayProfit] = useState(0);
  const [targetStats, setTargetStats] = useState({
    reached: 0,
    missed: 0,
  });
  const [productsSold, setProductsSold] = useState([]);
  const [monthlyProductData, setMonthlyProductData] = useState([]);
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [newTarget, setNewTarget] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productDateRange, setProductDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [todayOrders, setTodayOrders] = useState({
    completed: 0,
    processing: 0,
    onHold: 0,
    totalRevenue: 0,
  });
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  const db = getFirestore();

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([initializeDailyTarget(), fetchData()]);
      setLoading(false);
    };
    initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, productDateRange]);

  const initializeDailyTarget = async () => {
    try {
      const targetRef = doc(db, "Daily-target", "1");
      const targetDoc = await getDoc(targetRef);

      if (targetDoc.exists()) {
        setDailyTarget(targetDoc.data().amount || 0);
      } else {
        const defaultTarget = {
          amount: 100,
          createdAt: new Date().toISOString(),
        };
        await setDoc(targetRef, defaultTarget);
        setDailyTarget(defaultTarget.amount);
      }
    } catch (error) {
      console.error("Error initializing daily target:", error);
      setError("Failed to initialize daily target");
    }
  };

  const fetchData = async () => {
    try {
      const [salesSnapshot, adsSnapshot] = await Promise.all([
        getDocs(collection(db, "Sales")),
        getDocs(collection(db, "Ads")),
      ]);

      const salesData = salesSnapshot.docs.map((doc) => doc.data());
      const adsData = adsSnapshot.docs.map((doc) => doc.data());

      // Process today's orders
      const today = new Date().toISOString().split("T")[0];
      const todaySales = salesData.filter((sale) => sale.date === today);

      // Process products sold today
      const todayProducts = {};
      todaySales.forEach((sale) => {
        const productName = sale.productName || "Unknown Product";
        const quantity = parseInt(sale.amount) || 1;

        if (!todayProducts[productName]) {
          todayProducts[productName] = { count: 0, total: 0 };
        }
        todayProducts[productName].count += quantity;
        todayProducts[productName].total +=
          (parseFloat(sale.price) || 0) * quantity;
      });

      const productsSoldList = Object.entries(todayProducts)
        .map(([name, data]) => ({
          name,
          count: data.count,
          total: data.total,
        }))
        .sort((a, b) => b.count - a.count);

      setProductsSold(productsSoldList);

      // Process monthly product data
      const monthlyProductPerformance = processMonthlyProductData(salesData);
      setMonthlyProductData(monthlyProductPerformance);

      const todayStats = {
        completed: todaySales.filter((sale) => sale.status === "Completed")
          .length,
        processing: todaySales.filter((sale) => sale.status === "Processing")
          .length,
        onHold: todaySales.filter((sale) => sale.status === "OnHold").length,
        totalRevenue: todaySales.reduce((sum, sale) => {
          const price = parseFloat(sale.price) || 0;
          const amount = parseInt(sale.amount) || 1;
          return sum + price * amount;
        }, 0),
      };

      setTodayOrders(todayStats);
      setTodayProfit(todayStats.totalRevenue);

      const monthlyStats = processMonthlyData(salesData, adsData);
      const dailyStats = processDailyData(salesData);

      setMonthlyData(monthlyStats);
      setDailyData(dailyStats);
      updateTargetStats(salesData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch sales data");
    }
  };

  const processDailyData = (sales) => {
    const dailyStats = {};

    // Initialize all dates in range using UTC noon to avoid local-timezone day-shift
    const d = new Date(dateRange.start + "T12:00:00Z");
    while (true) {
      const dateStr = d.toISOString().split("T")[0];
      if (dateStr > dateRange.end) break;
      dailyStats[dateStr] = {
        date: dateStr,
        completed: 0,
        processing: 0,
        onHold: 0,
        total: 0,
      };
      d.setUTCDate(d.getUTCDate() + 1);
    }

    // Process sales
    sales.forEach((sale) => {
      // Normalize date: strip time portion in case sale.date is a full ISO string
      const saleDate = (sale.date || "").split("T")[0].trim();
      if (saleDate >= dateRange.start && saleDate <= dateRange.end) {
        if (!dailyStats[saleDate]) return;

        const price = parseFloat(sale.price) || 0;
        const amount = parseInt(sale.amount) || 1;
        const revenue = price * amount;
        const status = (sale.status || "").toLowerCase();

        if (status === "completed") {
          dailyStats[saleDate].completed += revenue;
        } else if (status === "processing") {
          dailyStats[saleDate].processing += revenue;
        } else if (status === "onhold") {
          dailyStats[saleDate].onHold += revenue;
        }

        dailyStats[saleDate].total += revenue;
      }
    });

    return Object.values(dailyStats).map((stat) => ({
      date: stat.date, // keep as YYYY-MM-DD for correct tooltip parsing
      completed: Number(stat.completed.toFixed(2)),
      processing: Number(stat.processing.toFixed(2)),
      onhold: Number(stat.onHold.toFixed(2)),
      total: Number(stat.total.toFixed(2)),
    }));
  };

  const processMonthlyProductData = (sales) => {
    const startDate = new Date(productDateRange.start);
    const endDate = new Date(productDateRange.end);

    // Filter for custom date range sales
    const filteredSales = sales.filter((sale) => {
      const saleDate = new Date(sale.date);
      return (
        saleDate >= startDate &&
        saleDate <= endDate &&
        sale.status === "Completed"
      );
    });

    // Group by product
    const productStats = {};

    filteredSales.forEach((sale) => {
      const productName = sale.productName || "Unknown Product";
      const quantity = parseInt(sale.amount) || 1;
      const total = (parseFloat(sale.price) || 0) * quantity;

      if (!productStats[productName]) {
        productStats[productName] = {
          quantity: 0,
          total: 0,
        };
      }

      productStats[productName].quantity += quantity;
      productStats[productName].total += total;
    });

    // Convert to array and sort by quantity
    return Object.entries(productStats)
      .map(([name, stats]) => ({
        name,
        quantity: stats.quantity,
        total: Number(stats.total.toFixed(2)),
      }))
      .sort((a, b) => b.quantity - a.quantity);
  };

  const processMonthlyData = (sales, ads) => {
    const monthlyStats = {};

    // Process sales
    sales.forEach((sale) => {
      if (sale.status === "Completed") {
        const date = new Date(sale.date);
        if (!isNaN(date.getTime())) {
          const monthKey = `${date.getFullYear()}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}`;

          if (!monthlyStats[monthKey]) {
            monthlyStats[monthKey] = {
              revenue: 0,
              expenses: 0,
              profit: 0,
              date: new Date(date.getFullYear(), date.getMonth(), 1),
            };
          }

          const price = parseFloat(sale.price) || 0;
          const amount = parseInt(sale.amount) || 1;
          monthlyStats[monthKey].revenue += price * amount;
        }
      }
    });

    // Process ads
    ads.forEach((ad) => {
      const date = new Date(ad.date);
      if (!isNaN(date.getTime())) {
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;

        if (!monthlyStats[monthKey]) {
          monthlyStats[monthKey] = {
            revenue: 0,
            expenses: 0,
            profit: 0,
            date: new Date(date.getFullYear(), date.getMonth(), 1),
          };
        }

        monthlyStats[monthKey].expenses += parseFloat(ad.amount) || 0;
      }
    });

    // Calculate profit and convert to array
    return Object.entries(monthlyStats)
      .map(([key, stats]) => ({
        month: stats.date.toISOString(), // Store as ISO string for consistent parsing
        revenue: Number(stats.revenue.toFixed(2)),
        expenses: Number(stats.expenses.toFixed(2)),
        profit: Number((stats.revenue - stats.expenses).toFixed(2)),
      }))
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  };

  const updateTargetStats = (sales) => {
    const processedDates = new Set();
    let reached = 0;
    let missed = 0;

    sales.forEach((sale) => {
      if (!processedDates.has(sale.date)) {
        const dailyRevenue = sales
          .filter((s) => s.date === sale.date)
          .reduce((sum, s) => {
            const price = parseFloat(s.price) || 0;
            const amount = parseInt(s.amount) || 1;
            return sum + price * amount;
          }, 0);

        if (dailyRevenue >= dailyTarget) {
          reached++;
        } else {
          missed++;
        }
        processedDates.add(sale.date);
      }
    });

    setTargetStats({ reached, missed });
  };

  const handleTargetUpdate = async () => {
    try {
      const newTargetValue = parseFloat(newTarget);
      if (isNaN(newTargetValue) || newTargetValue < 0) {
        throw new Error("Please enter a valid positive number");
      }

      const targetRef = doc(db, "Daily-target", "1");
      await setDoc(targetRef, {
        amount: newTargetValue,
        updatedAt: new Date().toISOString(),
      });

      setDailyTarget(newTargetValue);
      setIsEditingTarget(false);
      setNewTarget("");
    } catch (error) {
      console.error("Error updating target:", error);
      setError(error.message || "Failed to update target");
    }
  };

  const getProgressIcon = (progress) => {
    if (progress >= 100) {
      return <PartyPopper className="w-6 h-6 text-yellow-500 animate-bounce" />;
    } else if (progress >= 50) {
      return <Meh className="w-6 h-6 text-blue-500" />;
    } else if (progress >= 15) {
      return <Droplets className="w-6 h-6 text-blue-300" />;
    } else {
      return <AlertCircle className="w-6 h-6 text-red-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  const totalOrders =
    todayOrders.completed + todayOrders.processing + todayOrders.onHold;
  const progressPercentage = (todayOrders.totalRevenue / dailyTarget) * 100;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Sales Target</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      {/* Daily Target Progress */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-semibold">
                Daily Target Progress - {currentDate}
              </h3>
              {getProgressIcon(progressPercentage)}
            </div>
            {isEditingTarget ? (
              <div className="flex gap-2">
                <input
                  type="number"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  className="px-3 py-2 border rounded"
                  placeholder="Enter target"
                  min="0"
                  step="0.01"
                />
                <button
                  onClick={handleTargetUpdate}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsEditingTarget(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setNewTarget(dailyTarget.toString());
                  setIsEditingTarget(true);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Edit Target
              </button>
            )}
          </div>

          {/* Today's Orders Status */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-green-700 font-semibold">Completed</div>
              <div className="text-2xl font-bold text-green-800">
                {todayOrders.completed}
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-blue-700 font-semibold">Processing</div>
              <div className="text-2xl font-bold text-blue-800">
                {todayOrders.processing}
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-yellow-700 font-semibold">On Hold</div>
              <div className="text-2xl font-bold text-yellow-800">
                {todayOrders.onHold}
              </div>
            </div>
          </div>

          <div className="h-4 bg-gray-200 rounded-full overflow-hidden flex">
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{
                width: `${(todayOrders.completed / totalOrders) * 100 || 0}%`,
              }}
            />
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{
                width: `${(todayOrders.processing / totalOrders) * 100 || 0}%`,
              }}
            />
            <div
              className="h-full bg-yellow-500 transition-all duration-300"
              style={{
                width: `${(todayOrders.onHold / totalOrders) * 100 || 0}%`,
              }}
            />
          </div>

          <div className="mt-2 text-sm text-gray-600">
            Progress: ${todayOrders.totalRevenue.toFixed(2)} / $
            {dailyTarget.toFixed(2)}({Math.round(progressPercentage)}%)
          </div>

          {/* Progress bar legend */}
          <div className="mt-2 flex gap-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span>Completed ({todayOrders.completed})</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span>Processing ({todayOrders.processing})</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
              <span>On Hold ({todayOrders.onHold})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics - Moved under Daily Target Progress */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-green-700 font-semibold">
              Daily Average Revenue
            </div>
            <div className="text-2xl font-bold text-green-800">
              $
              {(
                dailyData.reduce((sum, day) => sum + day.total, 0) /
                dailyData.length
              ).toFixed(2)}
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-blue-700 font-semibold">
              Target Achievement Rate
            </div>
            <div className="text-2xl font-bold text-blue-800">
              {(
                (targetStats.reached /
                  (targetStats.reached + targetStats.missed || 1)) *
                100
              ).toFixed(1)}
              %
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-purple-700 font-semibold">
              Total Orders Today
            </div>
            <div className="text-2xl font-bold text-purple-800">
              {todayOrders.completed +
                todayOrders.processing +
                todayOrders.onHold}
            </div>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg">
            <div className="text-amber-700 font-semibold">
              Products Sold Today
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {productsSold.reduce((sum, product) => sum + product.count, 0)}
            </div>
          </div>
        </div>

        {/* Products Sold Today */}
        {productsSold.length > 0 && (
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-700">
              Products Sold Today
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {productsSold.map((product, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {product.count}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        ${product.total.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Daily Performance */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Daily Performance</h3>
        <div className="flex gap-4 mb-4">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, start: e.target.value }))
            }
            className="px-3 py-2 border rounded"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, end: e.target.value }))
            }
            className="px-3 py-2 border rounded"
          />
        </div>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => {
                  const [year, month, day] = value.split("-").map(Number);
                  return new Date(year, month - 1, day).toLocaleDateString("default", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="completed"
                stackId="a"
                fill="#4CAF50"
                name="Completed"
              />
              <Bar
                dataKey="processing"
                stackId="a"
                fill="#2196F3"
                name="Processing"
              />
              <Bar dataKey="onhold" stackId="a" fill="#FFC107" name="On Hold" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Performance */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Monthly Performance</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("default", {
                    month: "short",
                    year: "2-digit",
                  });
                }}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="revenue" fill="#4CAF50" name="Revenue" />
              <Bar dataKey="expenses" fill="#f44336" name="Expenses" />
              <Bar dataKey="profit" fill="#2196F3" name="Profit" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Product Performance */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Monthly Product Performance</h3>
          <div className="flex gap-4">
            <input
              type="date"
              value={productDateRange.start}
              onChange={(e) =>
                setProductDateRange((prev) => ({
                  ...prev,
                  start: e.target.value,
                }))
              }
              className="px-3 py-2 border rounded"
            />
            <input
              type="date"
              value={productDateRange.end}
              onChange={(e) =>
                setProductDateRange((prev) => ({
                  ...prev,
                  end: e.target.value,
                }))
              }
              className="px-3 py-2 border rounded"
            />
          </div>
        </div>
        {monthlyProductData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity Sold
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {monthlyProductData.map((product, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {product.quantity}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      ${product.total.toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-blue-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                    {monthlyProductData.reduce((sum, p) => sum + p.quantity, 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                    $
                    {monthlyProductData
                      .reduce((sum, p) => sum + p.total, 0)
                      .toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No products sold in this period
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTarget;
